import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { tenantToDetail } from "@/lib/operationsMappers";
import { PlatformPulseSection } from "@/components/dashboard/PlatformPulseCard";
import { StorageHealthPanel } from "@/components/dashboard/StorageHealthPanel";
import { OperationsAIAssistant } from "@/components/dashboard/OperationsAIAssistant";
import { ServiceHealthGrid } from "@/components/dashboard/ServiceHealthCard";
import { IncidentWorkspace } from "@/components/dashboard/IncidentWorkspace";
import { AttentionList } from "@/components/dashboard/AttentionList";
import { TenantHealthTable } from "@/components/dashboard/TenantHealthTable";
import { TenantControlCenterDrawer } from "@/components/dashboard/TenantControlCenterDrawer";
import { ActivityTimeline } from "@/components/dashboard/ActivityTimeline";
import { usePlatformOperations, type OpsAuthState } from "@/hooks/useOpsAuth";
import { useOutletContext } from "react-router-dom";
import type { TenantHealthRow } from "@/types";

function operatorNameFromEmail(email?: string | null): string {
  if (!email) return "Operator";
  const local = email.split("@")[0] ?? "operator";
  const first = local.split(/[._-]/)[0] ?? local;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

export function DashboardPage() {
  const { profile, isAuthenticated, authLoading, login } = useOutletContext<OpsAuthState>();
  const opsEnabled = isAuthenticated && !authLoading;
  const ops = usePlatformOperations(opsEnabled);
  const [searchParams, setSearchParams] = useSearchParams();

  const [incidentId, setIncidentId] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<TenantHealthRow | null>(null);

  const tenantDetail = useMemo(
    () => (selectedTenant ? tenantToDetail(selectedTenant) : null),
    [selectedTenant],
  );

  // Keep the open tenant drawer in sync after remediation refreshes fleet health.
  useEffect(() => {
    if (!selectedTenant) return;
    const next = ops.tenants.find((row) => row.slug === selectedTenant.slug);
    if (next && next !== selectedTenant) {
      setSelectedTenant(next);
    }
  }, [ops.tenants, selectedTenant]);

  const tenantAttentionItems = useMemo(() => {
    if (!selectedTenant) return [];
    return ops.attention.filter((item) => item.tenantSlug === selectedTenant.slug);
  }, [selectedTenant, ops.attention]);

  const tenantDoctorMessage = useMemo(() => {
    return tenantAttentionItems[0]?.problem?.trim() || null;
  }, [tenantAttentionItems]);

  const viewIncident = (id: string) => {
    setIncidentId(id);
    setSearchParams({ incident: id });
  };

  const closeIncident = () => {
    setIncidentId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("incident");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const deepLink = searchParams.get("incident");
    if (deepLink && opsEnabled) {
      setIncidentId(deepLink);
    }
  }, [searchParams, opsEnabled]);

  const dataLoading = authLoading || (ops.loading && ops.pulse == null);

  const handleLogin = async (email: string, password: string) => {
    await login(email, password);
    await ops.refresh();
  };

  return (
    <>
      <header className="page-head">
        <h2>Operations Center</h2>
        <p>Resolve incidents with the Operations AI Assistant. Verification runs automatically.</p>
      </header>

      <div className="ops-home">
        <section className="dashboard-section" aria-label="Operations AI Assistant">
          <OperationsAIAssistant
            briefing={ops.assistant}
            operatorName={operatorNameFromEmail(profile?.email)}
            loading={dataLoading}
            onViewIncident={viewIncident}
          />
        </section>

        <section className="dashboard-section" aria-label="Platform status">
          <PlatformPulseSection
            pulse={ops.pulse}
            loading={dataLoading}
            error={ops.error}
            needsAuth={!authLoading && (!isAuthenticated || ops.needsAuth)}
            refreshing={ops.loading && ops.pulse != null}
            onRefresh={() => void ops.refresh()}
            onLogin={handleLogin}
            onOpenStorage={() => viewIncident("service:storage")}
          />
        </section>

        {ops.pulse?.storageHealth ? (
          <section className="dashboard-section" aria-label="Storage Health">
            <StorageHealthPanel
              storage={ops.pulse.storageHealth}
              onOpenDetails={() => viewIncident("service:storage")}
            />
          </section>
        ) : null}

        <section className="dashboard-section" aria-label="What requires attention">
          <h3 className="dashboard-section__title">What requires attention?</h3>
          <AttentionList
            items={ops.attention}
            loading={dataLoading}
            onOpenIncident={viewIncident}
          />
        </section>

        <section className="dashboard-section" aria-label="Platform services">
          <h3 className="dashboard-section__title">Platform services</h3>
          {ops.services.length === 0 && !dataLoading ? (
            <div className="glass-panel attention-empty">No data available</div>
          ) : (
            <ServiceHealthGrid
              services={ops.services}
              onSelectService={(service) => viewIncident(`service:${service.id}`)}
            />
          )}
        </section>

        <section className="dashboard-section" aria-label="Tenant health">
          <h3 className="dashboard-section__title">Tenant health</h3>
          <TenantHealthTable
            tenants={ops.tenants}
            loading={dataLoading}
            onSelectTenant={setSelectedTenant}
          />
        </section>

        <section className="dashboard-section" aria-label="Recent activity">
          <h3 className="dashboard-section__title">Recent activity</h3>
          <ActivityTimeline events={ops.activity} loading={dataLoading} />
        </section>
      </div>

      <IncidentWorkspace
        incidentId={incidentId}
        onClose={closeIncident}
        onLoad={ops.loadIncident}
        onAction={ops.executeAction}
        onBackgroundRefresh={() => ops.refreshBackground()}
      />
      <TenantControlCenterDrawer
        detail={tenantDetail}
        attentionItems={tenantAttentionItems}
        doctorMessage={tenantDoctorMessage}
        platformVerifications={ops.platformVerifications}
        onOpenIncident={viewIncident}
        onExecuteAction={ops.executeAction}
        onActionComplete={() => ops.refreshBackground()}
        onClose={() => setSelectedTenant(null)}
      />
    </>
  );
}
